import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextareaAutosize,
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import {
  DirectionsWalk as TravelIcon,
  AccountBalance as FinancialIcon,
  Favorite as LegacyIcon,
  LocalHospital as HealthcareIcon,
  Home as LifestyleIcon,
  Add as OtherIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import ConditionalRender from '../common/ConditionalRender';
import CurrencyInput from '../common/CurrencyInput';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const GoalsStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { goals, personal } = wizardData;
  const [targetIncomeError, setTargetIncomeError] = React.useState('');
  
  // Initialize progress tracking
  const { trackFieldChange } = useProgressTracking({
    stepName: 'Goals & Priorities',
    stepNumber: 5
  });

  const handleTargetIncomeChange = (value: number) => {
    updateWizardData({
      goals: {
        ...goals,
        targetIncome: value,
      },
    });

    // Clear error when user starts typing
    if (targetIncomeError && value > 0) {
      setTargetIncomeError('');
    }
  };

  const validateTargetIncome = () => {
    if (!goals.targetIncome || goals.targetIncome <= 0) {
      setTargetIncomeError('Annual Target Income must be greater than zero');
      return false;
    }
    setTargetIncomeError('');
    return true;
  };

  // Count selected goals
  const countSelectedGoals = () => {
    return Object.values(goals.selected).filter(selected => selected).length;
  };

  const handleGoalToggle = (goal: keyof typeof goals.selected) => {
    const isCurrentlySelected = goals.selected[goal];
    const currentCount = countSelectedGoals();

    // If trying to select a new goal and already have 3 selected, don't allow it
    if (!isCurrentlySelected && currentCount >= 3) {
      return;
    }

    updateWizardData({
      goals: {
        ...goals,
        selected: {
          ...goals.selected,
          [goal]: !goals.selected[goal],
        },
      },
      uiState: {
        ...wizardData.uiState,
        hasAnsweredGoalsQuestion: true,
      }
    });
  };

  const handleOtherGoalChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Limit to 80 characters
    const newValue = event.target.value;
    if (newValue.length <= 80) {
      updateWizardData({
        goals: {
          ...goals,
          otherGoalDescription: newValue,
        },
      });
    }
  };

  const goalCards = [
    {
      key: 'financialIndependence',
      title: 'Financial Independence',
      description: 'Achieve financial freedom and maintain your desired lifestyle',
      icon: <FinancialIcon sx={{ fontSize: 40 }} />,
    },
    {
      key: 'travel',
      title: 'Travel & Adventure',
      description: 'Explore new places and create lasting memories',
      icon: <TravelIcon sx={{ fontSize: 40 }} />,
    },
    {
      key: 'legacy',
      title: 'Leave a Legacy',
      description: 'Pass on wealth and values to future generations',
      icon: <LegacyIcon sx={{ fontSize: 40 }} />,
    },
    {
      key: 'healthcare',
      title: 'Healthcare Security',
      description: 'Ensure comprehensive healthcare coverage in retirement',
      icon: <HealthcareIcon sx={{ fontSize: 40 }} />,
    },
    {
      key: 'lifestyle',
      title: 'Lifestyle Maintenance',
      description: 'Maintain your current standard of living',
      icon: <LifestyleIcon sx={{ fontSize: 40 }} />,
    },
    {
      key: 'other',
      title: 'Other Goals',
      description: 'Additional personal retirement goals',
      icon: <OtherIcon sx={{ fontSize: 40 }} />,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Goals & Priorities
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Target Income Section */}
        <Box>
          <Typography variant="body1" paragraph>
            {personal.hasPartner
              ? "What is your combined target annual cashflow (after-tax) in retirement?"
              : "What is your target annual cashflow (after-tax) in retirement?"}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <CurrencyInput
                fullWidth
                label="Annual Cashflow (After-Tax)"
                value={goals.targetIncome || ''}
                onChange={handleTargetIncomeChange}
                error={!!targetIncomeError}
                helperText={targetIncomeError}
                onBlur={validateTargetIncome}
                required
              />
            </Box>
          </Box>
        </Box>

        {/* Goals Selection Section */}
        <Box>
          <Typography variant="body1" paragraph>
            Select your retirement goals (select up to three goals):
          </Typography>
          {countSelectedGoals() >= 3 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You have selected the maximum of 3 goals. To select a different goal, please deselect one first.
            </Typography>
          )}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}>
            {goalCards.map((card) => (
              <Card
                key={card.key}
                sx={{
                  height: '100%',
                  border: goals.selected[card.key as keyof typeof goals.selected]
                    ? '2px solid'
                    : '1px solid',
                  borderColor: goals.selected[card.key as keyof typeof goals.selected]
                    ? 'primary.main'
                    : 'grey.300',
                  backgroundColor: goals.selected[card.key as keyof typeof goals.selected]
                    ? 'rgba(25, 118, 210, 0.04)'
                    : 'inherit',
                  transition: 'all 0.2s ease-in-out',
                  opacity: !goals.selected[card.key as keyof typeof goals.selected] && countSelectedGoals() >= 3
                    ? 0.6
                    : 1,
                  cursor: !goals.selected[card.key as keyof typeof goals.selected] && countSelectedGoals() >= 3
                    ? 'not-allowed'
                    : 'pointer',
                  '&:hover': {
                    borderColor: !goals.selected[card.key as keyof typeof goals.selected] && countSelectedGoals() >= 3
                      ? 'grey.300'
                      : 'primary.main',
                    boxShadow: goals.selected[card.key as keyof typeof goals.selected]
                      ? '0 4px 8px rgba(0, 0, 0, 0.2)'
                      : (!goals.selected[card.key as keyof typeof goals.selected] && countSelectedGoals() >= 3
                          ? 'none'
                          : '0 2px 4px rgba(0, 0, 0, 0.1)'),
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleGoalToggle(card.key as keyof typeof goals.selected)}
                  sx={{
                    height: '100%',
                    padding: '4px',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}>
                      {goals.selected[card.key as keyof typeof goals.selected] && (
                        <CheckIcon
                          color="primary"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            fontSize: 24,
                            backgroundColor: 'white',
                            borderRadius: '50%'
                          }}
                        />
                      )}
                      {card.icon}
                      <Typography variant="h6" align="center">
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {card.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Other Goals Description */}
        <ConditionalRender show={goals.selected.other}>
          <Box>
            <Typography variant="body1" paragraph>
              Please describe your other retirement goals:
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextareaAutosize
                minRows={2}
                maxRows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  resize: 'none',
                }}
                value={goals.otherGoalDescription || ''}
                onChange={handleOtherGoalChange}
                placeholder="Briefly describe your additional retirement goals..."
                maxLength={80}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  backgroundColor: 'white',
                  padding: '0 4px',
                }}
              >
                {(goals.otherGoalDescription || '').length}/80
              </Typography>
            </Box>
          </Box>
        </ConditionalRender>
      </Box>
    </Box>
  );
};

export default GoalsStep; 